import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
  Tooltip,
  IconButton,
  useMediaQuery,
  Drawer,
  LinearProgress,
  Button,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import Nav from "./nav";

import { BASEURL } from "../../Constants";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BugReportIcon from "@mui/icons-material/BugReport";
import HistoryIcon from "@mui/icons-material/History";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TerminalIcon from "@mui/icons-material/Terminal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NotesIcon from "@mui/icons-material/Notes";

import JSON5 from "json5";

const BRAND = "#005CC5";

function safeNumber(val, fallback = 0) {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

function scoreVariant(scoreNum) {
  if (scoreNum >= 80) return "error";
  if (scoreNum >= 60) return "warning";
  return "success";
}

function normalizeSeverity(sev) {
  const s = String(sev || "").trim().toLowerCase();
  if (s === "critical" || s === "crit") return "critical";
  if (s === "high" || s === "higher") return "high";
  if (s === "medium" || s === "med") return "medium";
  if (s === "low") return "low";
  if (s === "info" || s === "informational" || s === "information") return "informational";
  return "informational";
}

function severityLabel(s) {
  const k = normalizeSeverity(s);
  if (k === "critical") return "Critical";
  if (k === "high") return "High";
  if (k === "medium") return "Medium";
  if (k === "low") return "Low";
  return "Informational";
}

function severityChipColor(sevKey) {
  const k = normalizeSeverity(sevKey);
  if (k === "critical" || k === "high") return "error";
  if (k === "medium") return "warning";
  if (k === "low") return "success";
  return "info";
}

function getFindingCounts(findings) {
  const base = { critical: 0, high: 0, medium: 0, low: 0, informational: 0 };
  const list = Array.isArray(findings) ? findings : [];
  for (const f of list) {
    const key = normalizeSeverity(f?.severity);
    base[key] = (base[key] || 0) + 1;
  }
  return base;
}

function SeverityPills({ counts }) {
  const c = counts || { critical: 0, high: 0, medium: 0, low: 0, informational: 0 };
  const items = [
    { k: "critical", label: "Critical" },
    { k: "high", label: "High" },
    { k: "medium", label: "Medium" },
    { k: "low", label: "Low" },
    { k: "informational", label: "Info" },
  ];

  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
      {items.map(({ k, label }) => {
        const count = safeNumber(c[k], 0);
        const filled = count > 0;
        return (
          <Chip
            key={k}
            size="small"
            label={`${label}: ${count}`}
            color={severityChipColor(k)}
            variant={filled ? "filled" : "outlined"}
            sx={{
              fontWeight: 900,
              height: 24,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        );
      })}
    </Stack>
  );
}

/**
 * Optional: cache so we don't re-parse the same string 30 times per re-render
 */
const __cmdArrayCache = new Map();

/**
 * Convert string array => JS array
 * Handles JSON arrays and mixed quote arrays safely.
 * Never throws, never infinite-loops.
 */
function tryParseCommandArray(raw) {
  try {
    if (Array.isArray(raw)) return raw.map(String);
    if (raw == null) return null;

    let s = typeof raw === "string" ? raw : String(raw);
    s = s.trim();
    if (!s) return null;

    s = s.replace(/^Commands:\s*/i, "").trim();

    const cacheKey = s;
    if (__cmdArrayCache.has(cacheKey)) return __cmdArrayCache.get(cacheKey);

    if (s.length > 200_000) {
      __cmdArrayCache.set(cacheKey, null);
      return null;
    }

    const first = s.indexOf("[");
    const last = s.lastIndexOf("]");
    if (first === -1 || last === -1 || last <= first) {
      __cmdArrayCache.set(cacheKey, null);
      return null;
    }
    const listStr = s.slice(first, last + 1);

    try {
      const arr = JSON.parse(listStr);
      if (Array.isArray(arr)) {
        const res = arr.map(String);
        __cmdArrayCache.set(cacheKey, res);
        return res;
      }
    } catch (_) {}

    const out = [];
    const end = listStr.length - 1;
    let i = 1;

    while (i < end) {
      while (i < end) {
        const c = listStr[i];
        if (c === "," || c === " " || c === "\n" || c === "\t" || c === "\r") i++;
        else break;
      }
      if (i >= end) break;

      const q = listStr[i];
      if (q !== '"' && q !== "'") {
        while (i < end && listStr[i] !== "," && listStr[i] !== "]") i++;
        continue;
      }

      i++;
      let buf = "";

      while (i < end) {
        const ch = listStr[i];

        if (ch === "\\") {
          const next = listStr[i + 1];
          if (next == null) {
            i++;
            break;
          }
          if (next === "n") buf += "\n";
          else if (next === "t") buf += "\t";
          else if (next === "r") buf += "\r";
          else buf += next;
          i += 2;
          continue;
        }

        if (ch === q) {
          i++;
          break;
        }

        buf += ch;
        i++;
      }

      out.push(buf);
    }

    const res = out.length ? out : null;
    __cmdArrayCache.set(cacheKey, res);
    return res;
  } catch (e) {
    return null;
  }
}

function parseHistory(history = []) {
  return history.map((entry, idx) => {
    const trimmed = String(entry || "").trim();

    const mk = (type, content, raw = entry) => ({
      id: `${idx}-${type}`,
      type,
      content,
      raw,
      idx,
    });

    if (trimmed.startsWith("Suggestion:")) {
      return mk("suggestion", trimmed.replace(/^Suggestion:\s*/i, ""));
    }

    if (trimmed.startsWith("Commands Observation:")) {
      return mk("observation", trimmed.replace(/^Commands Observation:\s*/i, ""));
    }

    if (trimmed.startsWith("Observation:")) {
      return mk("observation", trimmed.replace(/^Observation:\s*/i, ""));
    }

    if (trimmed.startsWith("Commands:")) {
      const raw = trimmed.replace(/^Commands:\s*/i, "").trim();

      // 1) JSON5 parse if it's actually JSON
      try {
        const parsed = JSON5.parse(raw);
        if (Array.isArray(parsed)) return mk("commands", parsed.map(String));
        return mk("commands", parsed);
      } catch (_) {
        // 2) fallback: try to pull a quoted array from a bigger string
        const arr = tryParseCommandArray(raw);
        if (Array.isArray(arr)) return mk("commands", arr);
        return mk("commands", raw);
      }
    }

    return mk("note", trimmed);
  });
}

function CodeBlock({ value }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        bgcolor: "#050B14",
        borderColor: "rgba(0, 92, 197, 0.35)",
        boxShadow: "0 0 0 1px rgba(0, 92, 197, 0.10) inset",
        overflowX: "auto",
        borderRadius: 2,
      }}
    >
      <Box
        component="pre"
        sx={{
          m: 0,
          fontSize: 12,
          lineHeight: 1.5,
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          color: "rgba(210, 235, 255, 0.95)",
        }}
      >
        {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
      </Box>
    </Paper>
  );
}

/**
 * Cyber-style "Agent Log Entry"
 */
function AgentLogCard({ step }) {
  const isSuggestion = step.type === "suggestion";
  const isCommands = step.type === "commands";
  const isObservation = step.type === "observation";

  const meta = (() => {
    if (isSuggestion) {
      return {
        title: "AI Suggestion",
        icon: <AutoAwesomeIcon fontSize="small" />,
        border: "rgba(0, 92, 197, 0.40)",
        glow: "rgba(0, 92, 197, 0.16)",
        labelColor: BRAND,
        badge: "AI",
      };
    }
    if (isCommands) {
      return {
        title: "Executed Commands",
        icon: <TerminalIcon fontSize="small" />,
        border: "rgba(0, 92, 197, 0.40)",
        glow: "rgba(0, 92, 197, 0.16)",
        labelColor: BRAND,
        badge: "EXEC",
      };
    }
    if (isObservation) {
      return {
        title: "Observation / Telemetry",
        icon: <VisibilityIcon fontSize="small" />,
        border: "rgba(0, 170, 80, 0.35)",
        glow: "rgba(0, 170, 80, 0.10)",
        labelColor: "rgba(0, 170, 80, 0.95)",
        badge: "OBS",
      };
    }
    return {
      title: "Agent Note",
      icon: <NotesIcon fontSize="small" />,
      border: "rgba(0, 92, 197, 0.22)",
      glow: "rgba(0, 92, 197, 0.10)",
      labelColor: BRAND,
      badge: "NOTE",
    };
  })();

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: meta.border,
        bgcolor: "#FFFFFF",
        boxShadow: `0 0 0 1px ${meta.glow} inset, 0 14px 34px rgba(0,0,0,0.06)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.22,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(0,92,197,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(0,92,197,0.08) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
          maskImage: "radial-gradient(circle at 35% 30%, black 35%, transparent 78%)",
        }}
      />

      <CardContent sx={{ position: "relative" }}>
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(0, 92, 197, 0.06)",
                  border: `1px solid ${meta.border}`,
                  boxShadow: `0 0 18px ${meta.glow}`,
                  color: meta.labelColor,
                }}
              >
                {meta.icon}
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 900, fontSize: 13, color: meta.labelColor }}>
                  {meta.title}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.65, color: "#000" }}>
                  Step {String(step.idx + 1).padStart(2, "0")} • agent-run log
                </Typography>
              </Box>
            </Stack>

            <Chip
              size="small"
              label={meta.badge}
              sx={{
                height: 22,
                fontWeight: 900,
                letterSpacing: 0.6,
                bgcolor: "rgba(0, 92, 197, 0.06)",
                border: `1px solid ${meta.border}`,
                color: meta.labelColor,
              }}
            />
          </Stack>

          {isCommands ? (
            Array.isArray(step.content) ? (
              <Stack spacing={0.85}>
                {step.content.map((cmd, cIdx) => (
                  <Box key={`${step.id}-${cIdx}`}>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.8, display: "block", mb: 0.5, color: "#000" }}
                    >
                      Command {cIdx + 1}
                    </Typography>
                    <CodeBlock value={cmd} />
                  </Box>
                ))}
              </Stack>
            ) : (
              <CodeBlock value={step.content} />
            )
          ) : (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                lineHeight: 1.6,
                color: "#0B0F16",
              }}
            >
              {step.content}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function FindingDrawer({ open, onClose, finding }) {
  const sev = normalizeSeverity(finding?.severity);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "92vw", sm: 520 },
          p: 2,
          bgcolor: "#fff",
        },
      }}
    >
      <Stack spacing={2} sx={{ height: "100%" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="overline" sx={{ color: BRAND, fontWeight: 900, letterSpacing: 1 }}>
              Finding Details
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 950, color: "#000", lineHeight: 1.1 }}>
              {finding?.name || "Finding"}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}>
              <Chip
                size="small"
                label={severityLabel(sev).toUpperCase()}
                color={severityChipColor(sev)}
                variant="filled"
                sx={{ fontWeight: 900 }}
              />
              {Array.isArray(finding?.location) && finding.location[0] ? (
                <Chip
                  size="small"
                  label={finding.location[0]}
                  variant="outlined"
                  sx={{
                    borderColor: "rgba(0,92,197,0.25)",
                    bgcolor: "rgba(0,92,197,0.04)",
                    maxWidth: "100%",
                  }}
                />
              ) : null}
            </Stack>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              border: "1px solid rgba(0,92,197,0.25)",
              borderRadius: 2,
              bgcolor: "rgba(0,92,197,0.04)",
              color: BRAND,
            }}
          >
            <CancelIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ overflow: "auto", pr: 0.5 }}>
          <Stack spacing={2}>
            {finding?.description ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "#000", mb: 0.5 }}>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#000" }}>
                  {finding.description}
                </Typography>
              </Box>
            ) : null}

            {finding?.details ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "#000", mb: 0.5 }}>
                  Details
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#000" }}>
                  {finding.details}
                </Typography>
              </Box>
            ) : null}

            {finding?.evidence ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "#000", mb: 0.5 }}>
                  Evidence
                </Typography>
                <CodeBlock value={finding.evidence} />
              </Box>
            ) : null}

            {finding?.remediation ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "#000", mb: 0.5 }}>
                  Remediation
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#000" }}>
                  {finding.remediation}
                </Typography>
              </Box>
            ) : null}

            {!finding?.description && !finding?.details && !finding?.evidence && !finding?.remediation ? (
              <Alert severity="info">No additional details were provided for this finding.</Alert>
            ) : null}
          </Stack>
        </Box>

        <Box sx={{ mt: "auto" }}>
          <Button
            onClick={onClose}
            fullWidth
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              borderColor: "rgba(0,92,197,0.30)",
              color: BRAND,
            }}
          >
            Close
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
}

export default function ExternalAttackHistory() {
  const { pentest_id } = useParams();

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  // show 3 cards on desktop
  const visibleCount = isMdUp ? 3 : isSmUp ? 2 : 1;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [assets, setAssets] = useState([]);
  const [selectedHost, setSelectedHost] = useState("");

  const [attackSearch, setAttackSearch] = useState("");

  // Attack selection/paging state
  const [selectedAttackName, setSelectedAttackName] = useState("");
  const [attackPageStart, setAttackPageStart] = useState(0);

  // Finding drawer
  const [findingDrawerOpen, setFindingDrawerOpen] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);

  useEffect(() => {
    let mounted = true;
    const token = sessionStorage.getItem("token");

    async function loadAssets() {
      setLoading(true);
      setErr("");

      try {
        const url = `${BASEURL}/pentest/assets?pentest_id=${encodeURIComponent(pentest_id || "")}`;
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });
        const data = await res.json();

        if (!mounted) return;

        if (!data?.success) {
          setErr(data?.message || "Failed to load assets");
          setAssets([]);
          setLoading(false);
          return;
        }

        const list = Array.isArray(data.assets) ? data.assets : [];
        setAssets(list);

        if (list.length > 0) {
          setSelectedHost((prev) => prev || list[0].host);
        }

        setLoading(false);
      } catch (e) {
        if (!mounted) return;
        setErr(e?.message || "Failed to load assets");
        setAssets([]);
        setLoading(false);
      }
    }

    loadAssets();

    return () => {
      mounted = false;
    };
  }, [pentest_id]);

  const selectedAsset = useMemo(() => {
    if (!assets?.length) return null;
    return assets.find((a) => a.host === selectedHost) || assets[0] || null;
  }, [assets, selectedHost]);

  const threatModel = selectedAsset?.threat_model || null;

  const attacks = useMemo(() => {
    const list = threatModel?.attack_score || [];
    if (!Array.isArray(list)) return [];

    const q = attackSearch.trim().toLowerCase();
    if (!q) return list;

    return list.filter((a) => String(a?.name || "").toLowerCase().includes(q));
  }, [threatModel, attackSearch]);

  const summary = useMemo(() => {
    const total = attacks.length;
    const vulnerableCount = attacks.filter((a) => a?.exploit_results?.vulnerable).length;
    const findingCount = attacks.reduce((acc, a) => {
      const f = a?.exploit_results?.findings;
      return acc + (Array.isArray(f) ? f.length : 0);
    }, 0);

    return { total, vulnerableCount, findingCount };
  }, [attacks]);

  const assetMiniStats = useMemo(() => {
    const map = new Map();
    for (const a of assets) {
      const list = Array.isArray(a?.threat_model?.attack_score) ? a.threat_model.attack_score : [];
      const totalAttacks = list.length;
      const vuln = list.filter((x) => !!x?.exploit_results?.vulnerable).length;
      const findings = list.reduce((acc, x) => {
        const f = x?.exploit_results?.findings;
        return acc + (Array.isArray(f) ? f.length : 0);
      }, 0);
      map.set(a.host, { totalAttacks, vuln, findings });
    }
    return map;
  }, [assets]);

  useEffect(() => {
    if (!attacks.length) {
      setSelectedAttackName("");
      setAttackPageStart(0);
      return;
    }

    const stillExists = attacks.some((a) => a?.name === selectedAttackName);
    if (!stillExists) {
      setSelectedAttackName(attacks[0]?.name || "");
      setAttackPageStart(0);
    }
  }, [attacks, selectedAttackName]);

  const maxPageStart = useMemo(() => {
    if (!attacks.length) return 0;
    return Math.max(0, Math.floor((attacks.length - 1) / visibleCount) * visibleCount);
  }, [attacks.length, visibleCount]);

  const visibleAttacks = useMemo(() => {
    return attacks.slice(attackPageStart, attackPageStart + visibleCount);
  }, [attacks, attackPageStart, visibleCount]);

  const selectedAttack = useMemo(() => {
    if (!attacks.length) return null;
    return attacks.find((a) => a?.name === selectedAttackName) || attacks[0] || null;
  }, [attacks, selectedAttackName]);

  useEffect(() => {
    if (!selectedAttackName || !attacks.length) return;

    const idx = attacks.findIndex((a) => a?.name === selectedAttackName);
    if (idx === -1) return;

    const end = attackPageStart + visibleCount - 1;

    if (idx < attackPageStart || idx > end) {
      const snapped = Math.floor(idx / visibleCount) * visibleCount;
      setAttackPageStart(Math.min(maxPageStart, Math.max(0, snapped)));
    }
  }, [selectedAttackName, attacks, attackPageStart, visibleCount, maxPageStart]);

  const handlePrevPage = () => {
    const newStart = Math.max(0, attackPageStart - visibleCount);
    setAttackPageStart(newStart);
    const nextSelected = attacks[newStart]?.name || "";
    if (nextSelected) setSelectedAttackName(nextSelected);
  };

  const handleNextPage = () => {
    const newStart = Math.min(maxPageStart, attackPageStart + visibleCount);
    setAttackPageStart(newStart);
    const nextSelected = attacks[newStart]?.name || "";
    if (nextSelected) setSelectedAttackName(nextSelected);
  };

  const selectedAttackExploit = selectedAttack?.exploit_results || {};
  const selectedAttackFindings = Array.isArray(selectedAttackExploit?.findings)
    ? selectedAttackExploit.findings
    : [];
  const selectedAttackHistory = Array.isArray(selectedAttackExploit?.history) ? selectedAttackExploit.history : [];
  const selectedAttackParsedHistory = useMemo(() => parseHistory(selectedAttackHistory), [selectedAttackHistory]);

  const selectedFindingCounts = useMemo(
    () => getFindingCounts(selectedAttackFindings),
    [selectedAttackFindings]
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={22} />
          <Typography>Loading threat model…</Typography>
        </Stack>
      </Box>
    );
  }

  if (err) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Make sure your API route matches the existing Assets page call.
        </Typography>
      </Box>
    );
  }

  const AttackSelectorCard = ({ attack, isSelected, onSelect }) => {
    const scoreNum = safeNumber(attack?.score, 0);
    const exploit = attack?.exploit_results || {};
    const findings = Array.isArray(exploit?.findings) ? exploit.findings : [];
    const history = Array.isArray(exploit?.history) ? exploit.history : [];
    const isSuccess = !!exploit?.vulnerable; // "worked" = exploit found during process
    const counts = getFindingCounts(findings);

    return (
      <Card
        variant="outlined"
        onClick={onSelect}
        sx={{
          cursor: "pointer",
          minWidth: isMdUp ? 380 : 320,
          borderRadius: 2.5,
          borderColor: isSelected ? BRAND : "rgba(0,92,197,0.22)",
          bgcolor: isSelected ? "rgba(0,92,197,0.06)" : "#FFFFFF",
          transition: "all 140ms ease",
          position: "relative",
          overflow: "hidden",
          boxShadow: isSelected
            ? "0 0 0 2px rgba(0,92,197,0.18), 0 18px 42px rgba(0,0,0,0.08)"
            : "0 14px 34px rgba(0,0,0,0.06)",
          "&:hover": {
            transform: "translateY(-2px)",
            borderColor: BRAND,
            boxShadow: "0 20px 48px rgba(0,0,0,0.10)",
          },
        }}
      >
        {/* subtle grid */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: isSelected ? 0.28 : 0.16,
            pointerEvents: "none",
            backgroundImage:
              "linear-gradient(rgba(0,92,197,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(0,92,197,0.08) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
            maskImage: "radial-gradient(circle at 30% 30%, black 40%, transparent 80%)",
          }}
        />
        <CardContent sx={{ position: "relative", py: 2, "&:last-child": { pb: 2 } }}>
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1.25} alignItems="flex-start" justifyContent="space-between">
              <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(0,92,197,0.06)",
                    border: "1px solid rgba(0,92,197,0.25)",
                    boxShadow: "0 0 18px rgba(0,92,197,0.12)",
                    color: BRAND,
                    flex: "0 0 auto",
                  }}
                >
                  <BugReportIcon fontSize="small" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 950, fontSize: 14, color: "#000" }} noWrap>
                    {attack?.name || "Unnamed Attack"}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.75, color: "#000" }}>
                    Click to load agent logs • {history.length} steps • {findings.length} findings
                  </Typography>
                </Box>
              </Stack>

              <Chip
                icon={isSuccess ? <CheckCircleIcon /> : <CancelIcon />}
                label={isSuccess ? "Exploit Found" : "No Exploit"}
                color={isSuccess ? "success" : "default"}
                variant={isSuccess ? "filled" : "outlined"}
                sx={{
                  fontWeight: 950,
                  borderColor: isSuccess ? undefined : "rgba(0,92,197,0.35)",
                  color: isSuccess ? undefined : BRAND,
                }}
              />
            </Stack>

            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
                <Chip
                  size="small"
                  label={`Score: ${scoreNum}`}
                  color={scoreVariant(scoreNum)}
                  variant="filled"
                  sx={{ height: 24, fontWeight: 950 }}
                />
                <Chip
                  size="small"
                  label={`Steps: ${history.length}`}
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontWeight: 950,
                    borderColor: "rgba(0,92,197,0.25)",
                    bgcolor: "rgba(0,92,197,0.03)",
                    color: BRAND,
                  }}
                />
                <Chip
                  size="small"
                  label={`Findings: ${findings.length}`}
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontWeight: 950,
                    borderColor: "rgba(0,92,197,0.25)",
                    bgcolor: "rgba(0,92,197,0.03)",
                    color: BRAND,
                  }}
                />
              </Stack>

              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, scoreNum))}
                  sx={{
                    height: 8,
                    borderRadius: 99,
                    bgcolor: "rgba(0,92,197,0.10)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 99,
                      bgcolor: "rgba(0,92,197,0.65)",
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Severity breakdown */}
            <SeverityPills counts={counts} />
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#fff" }}>
      <FindingDrawer
        open={findingDrawerOpen}
        onClose={() => setFindingDrawerOpen(false)}
        finding={selectedFinding}
      />

      <Stack spacing={2}>
        <Nav />

        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: "#000" }}>
              AI Agent Execution Logs
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, color: "#000" }}>
              Select an asset → select an attack → review exactly what the agent suggested, executed, and observed.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Chip
              icon={<BugReportIcon />}
              label={`${summary.total} attacks`}
              variant="outlined"
              sx={{
                borderColor: "rgba(0,92,197,0.35)",
                color: BRAND,
                fontWeight: 900,
              }}
            />
            <Chip
              icon={<CancelIcon />}
              label={`${summary.vulnerableCount} vulnerable`}
              color={summary.vulnerableCount > 0 ? "error" : "default"}
              variant={summary.vulnerableCount > 0 ? "filled" : "outlined"}
              sx={{
                fontWeight: 900,
                borderColor: summary.vulnerableCount > 0 ? undefined : "rgba(0,92,197,0.35)",
                color: summary.vulnerableCount > 0 ? undefined : BRAND,
              }}
            />
            <Chip
              label={`${summary.findingCount} findings`}
              variant="outlined"
              sx={{
                borderColor: "rgba(0,92,197,0.35)",
                color: BRAND,
                fontWeight: 900,
              }}
            />
          </Stack>
        </Stack>

        <Divider />

        <Grid container spacing={2}>
          {/* LEFT content */}
          <Grid item xs={12} md={9}>
            {/* Attack selector */}
            <Card
              variant="outlined"
              sx={{
                borderColor: "rgba(0,92,197,0.25)",
                boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
                borderRadius: 2,
              }}
            >
              <CardContent>
                {!attacks.length ? (
                  <Alert severity="info">
                    No attacks found in threat_model.attack_score for this asset.
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {/* Make it obvious */}
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderColor: "rgba(0,92,197,0.25)",
                        bgcolor: "rgba(0,92,197,0.03)",
                      }}
                    >
                      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: 2,
                              display: "grid",
                              placeItems: "center",
                              bgcolor: "rgba(0,92,197,0.06)",
                              border: "1px solid rgba(0,92,197,0.25)",
                              boxShadow: "0 0 18px rgba(0,92,197,0.12)",
                              color: BRAND,
                              flex: "0 0 auto",
                            }}
                          >
                            <HistoryIcon fontSize="small" />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 950, color: BRAND }}>
                              Select an Attack
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8, color: "#000" }}>
                              The page below is the agent’s execution timeline for the selected attack.
                              Click any attack card to switch the log view.
                            </Typography>
                            {selectedAttack ? (
                              <Typography variant="caption" sx={{ display: "block", opacity: 0.75, color: "#000", mt: 0.5 }}>
                                Selected: <b>{selectedAttack?.name || "Unnamed Attack"}</b>
                              </Typography>
                            ) : null}
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>

                    <TextField
                      value={attackSearch}
                      onChange={(e) => setAttackSearch(e.target.value)}
                      placeholder="Search attacks…"
                      size="small"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />

                    {/* Attack selector row w/ arrows */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        onClick={handlePrevPage}
                        disabled={attackPageStart <= 0}
                        size="small"
                        sx={{
                          border: "1px solid rgba(0,92,197,0.30)",
                          borderRadius: 2,
                          bgcolor: "rgba(0,92,197,0.04)",
                          color: BRAND,
                        }}
                      >
                        <ChevronLeftIcon />
                      </IconButton>

                      <Box sx={{ flex: 1, overflow: "hidden" }}>
                        <Stack direction="row" spacing={1.25}>
                          {visibleAttacks.map((attack, idx) => {
                            const isSelected = attack?.name === selectedAttackName;
                            return (
                              <AttackSelectorCard
                                key={`${attack?.name || "attack"}-${idx}`}
                                attack={attack}
                                isSelected={isSelected}
                                onSelect={() => {
                                  setSelectedAttackName(attack?.name || "");
                                  // close any open finding drawer when switching attacks
                                  setFindingDrawerOpen(false);
                                  setSelectedFinding(null);
                                }}
                              />
                            );
                          })}
                        </Stack>
                      </Box>

                      <IconButton
                        onClick={handleNextPage}
                        disabled={attackPageStart >= maxPageStart}
                        size="small"
                        sx={{
                          border: "1px solid rgba(0,92,197,0.30)",
                          borderRadius: 2,
                          bgcolor: "rgba(0,92,197,0.04)",
                          color: BRAND,
                        }}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Findings summary (click -> side drawer) */}
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: "rgba(0,92,197,0.25)",
                boxShadow: "0 14px 34px rgba(0,0,0,0.06)",
                mt: 2,
              }}
            >
              <CardContent>
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "rgba(0,92,197,0.06)",
                          border: "1px solid rgba(0,92,197,0.25)",
                          boxShadow: "0 0 18px rgba(0,92,197,0.12)",
                        }}
                      >
                        <BugReportIcon fontSize="small" sx={{ color: BRAND }} />
                      </Box>

                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 950, color: BRAND, lineHeight: 1 }}>
                          Findings Found
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.75, color: "#000" }}>
                          Click a finding to open full details in a side panel.
                        </Typography>
                      </Box>
                    </Stack>

                    <Chip
                      size="small"
                      label={`${selectedAttackFindings.length} total`}
                      variant="outlined"
                      sx={{
                        fontWeight: 950,
                        borderColor: "rgba(0,92,197,0.30)",
                        bgcolor: "rgba(0,92,197,0.04)",
                        color: BRAND,
                      }}
                    />
                  </Stack>

                  <SeverityPills counts={selectedFindingCounts} />

                  {selectedAttackFindings.length === 0 ? (
                    <Alert severity="success">No findings for this attack.</Alert>
                  ) : (
                    <Card
                      variant="outlined"
                      sx={{ borderRadius: 2, borderColor: "rgba(0,92,197,0.18)" }}
                    >
                      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                        <List dense sx={{ p: 0 }}>
                          {selectedAttackFindings.map((f, idx) => {
                            const sev = normalizeSeverity(f?.severity);
                            return (
                              <ListItemButton
                                key={`${f?.id || f?.name || idx}`}
                                onClick={() => {
                                  setSelectedFinding(f);
                                  setFindingDrawerOpen(true);
                                }}
                                sx={{
                                  borderRadius: 2,
                                  mb: 0.5,
                                  border: "1px solid rgba(0,92,197,0.10)",
                                  "&:hover": { borderColor: "rgba(0,92,197,0.30)" },
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Typography sx={{ fontWeight: 950, color: "#000" }} noWrap>
                                      {f?.name || "Finding"}
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography variant="caption" sx={{ opacity: 0.8, color: "#000" }}>
                                      Severity: <b>{severityLabel(sev)}</b>
                                    </Typography>
                                  }
                                />
                                <Chip
                                  size="small"
                                  label={severityLabel(sev).toUpperCase()}
                                  color={severityChipColor(sev)}
                                  variant="filled"
                                  sx={{ fontWeight: 950 }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Agent execution history (kept under selector + findings summary) */}
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: "rgba(0,92,197,0.25)",
                boxShadow: "0 14px 34px rgba(0,0,0,0.06)",
                mt: 2,
              }}
            >
              <CardContent>
                {!selectedAttack ? (
                  <Alert severity="info">Select an attack to view details.</Alert>
                ) : (
                  <Box>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(0,92,197,0.06)",
                        border: "1px solid rgba(0,92,197,0.25)",
                        boxShadow: "0 0 18px rgba(0,92,197,0.12)",
                      }}
                    >
                      <TerminalIcon fontSize="small" sx={{ color: BRAND }} />
                    </Box>

                    <Box sx={{ marginBottom: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 950, mb: 0.5, color: BRAND }}>
                        Agent Execution History
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, color: "#000" }}>
                        Timeline of what the agent suggested, ran, and observed for this attack.
                      </Typography>
                    </Box>

                    {selectedAttackParsedHistory.length === 0 ? (
                      <Alert severity="info">No history steps recorded.</Alert>
                    ) : (
                      <Stack spacing={1.25}>
                        {selectedAttackParsedHistory.map((step) => (
                          <AgentLogCard key={step.id} step={step} />
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* RIGHT assets list */}
          <Grid item xs={12} md={3}>
            <Card
              variant="outlined"
              sx={{
                borderColor: "rgba(0,92,197,0.25)",
                boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Stack spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 950, color: BRAND }}>
                    Assets / Endpoints
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.75, color: "#000" }}>
                    Select an asset to load its attacks + execution logs.
                  </Typography>
                </Stack>

                <Divider sx={{ my: 1.25 }} />

                <List dense sx={{ p: 0 }}>
                  {assets.map((a) => {
                    const isSelected = a.host === selectedHost;
                    const stats = assetMiniStats.get(a.host) || { totalAttacks: 0, vuln: 0, findings: 0 };

                    return (
                      <ListItemButton
                        key={a.host}
                        selected={isSelected}
                        onClick={() => {
                          setSelectedHost(a.host);
                          setAttackSearch("");
                          setAttackPageStart(0);
                          setSelectedAttackName("");
                          setFindingDrawerOpen(false);
                          setSelectedFinding(null);
                        }}
                        sx={{
                          borderRadius: 2,
                          mb: 0.75,
                          border: "1px solid rgba(0,92,197,0.12)",
                          "&.Mui-selected": {
                            bgcolor: "rgba(0,92,197,0.07)",
                            border: "1px solid rgba(0,92,197,0.35)",
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography sx={{ fontWeight: 950, color: "#000" }} noWrap>
                              {a.host}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={0.5} sx={{ mt: 0.25 }}>
                              <Typography variant="caption" sx={{ opacity: 0.75, color: "#000" }}>
                                Ports: {Array.isArray(a.ports) ? a.ports.join(", ") : a.ports || "—"}
                              </Typography>
                              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                                <Chip
                                  size="small"
                                  label={`${stats.totalAttacks} attacks`}
                                  variant="outlined"
                                  sx={{
                                    height: 22,
                                    fontWeight: 900,
                                    borderColor: "rgba(0,92,197,0.25)",
                                    bgcolor: "rgba(0,92,197,0.03)",
                                    color: BRAND,
                                  }}
                                />
                                <Chip
                                  size="small"
                                  label={`${stats.findings} findings`}
                                  variant="outlined"
                                  sx={{
                                    height: 22,
                                    fontWeight: 900,
                                    borderColor: "rgba(0,92,197,0.25)",
                                    bgcolor: "rgba(0,92,197,0.03)",
                                    color: BRAND,
                                  }}
                                />
                                <Chip
                                  size="small"
                                  icon={<CancelIcon fontSize="small" />}
                                  label={`${stats.vuln} vuln`}
                                  color={stats.vuln > 0 ? "error" : "default"}
                                  variant={stats.vuln > 0 ? "filled" : "outlined"}
                                  sx={{
                                    height: 22,
                                    fontWeight: 900,
                                    borderColor: stats.vuln > 0 ? undefined : "rgba(0,92,197,0.25)",
                                    color: stats.vuln > 0 ? undefined : BRAND,
                                  }}
                                />
                              </Stack>
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
